"use client";
import React, { useRef } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import Link from "next/link";

const AboutSection = ({ data }) => {
  // Fallback to hardcoded values if data is not available
  const title = data?.title || "خيارك الافضل للدعاية والاعلان في العراق مع اكبر";
  const titleHighlight = data?.subtitle || "اللوحات والشاشات الاعلانية";
  const description = data?.description || "تأسست شركة نخلة بابل سنة 2012 من قبل مجموعة من الشباب العراقيين لديهم الرغبة في تنفيذ ما يملكون من خبرات و مهارات في وطنهم، حيث لديهم خبرة طويلة في مجال التسويق و الاعلان الخاص بالشركات و المؤسسات العالمية الكبرى في مختلف الصناعات و القطّاعات.";
  const imageUrl = data?.image?.url || data?.imageUrl || "https://pub-0705ed3bb3a5425799ba64b1bd76a81b.r2.dev/shared_media/pob1.jpg";

  const sectionRef = useRef(null);
  const imageRef = useRef(null);
  const textRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const imageY = useTransform(scrollYProgress, [0, 1], ["10%", "-10%"]);
  const imageScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.1, 1, 0.9]);
  const textX = useTransform(scrollYProgress, [0, 0.5], ["50px", "0px"]);

  const isImageInView = useInView(imageRef, { once: true, margin: "-50px" });
  const isTextInView = useInView(textRef, { once: true, margin: "-50px" });

  return (
    <section ref={sectionRef} className="py-20  overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Image with Parallax */} 
          <motion.div
            ref={imageRef}
            className="relative order-2 lg:order-1 w-full"
            style={{ y: imageY }}
          >
            <motion.div
              className="relative overflow-hidden rounded-3xl shadow-2xl w-full"
              style={{ scale: imageScale }}
              initial={{ opacity: 0, x: -50 }}
              animate={isImageInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 1.2, ease: "easeOut" }}
            >
              <img
                src={imageUrl}
                alt="نخلة بابل"
                className="w-full h-[300px] md:h-[500px] object-cover"
                loading="eager"
                style={{ width: '100%', height: '100%', maxHeight: '100%', objectFit: 'cover' }}
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0a367b]/20 to-transparent"></div>

              {/* Floating Elements */}
              <motion.div
                className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-2xl p-3 shadow-lg"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <div className="text-lg font-bold text-[#0a367b]">2012</div>
                <div className="text-xs text-gray-600">تأسست</div>
              </motion.div>

              <motion.div
                className="absolute bottom-4 left-4 bg-[#dfa428]/90 backdrop-blur-sm rounded-2xl p-3 shadow-lg"
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 3, repeat: Infinity, delay: 1.5 }}
              >
                <div className="text-lg font-bold text-[#0a367b]">كن مميز</div>
                <div className="text-xs text-[#0a367b]">بأعلانك</div>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Content */}
          <motion.div
            ref={textRef}
            className="space-y-6 order-1 lg:order-2 w-full"
            style={{ x: textX }}
          >
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={isTextInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#233c78] mb-4 leading-tight font-cairo">
                {title}
                {titleHighlight && (
                  <span className="text-yellow-500">
                    {" "}
                    {titleHighlight}
                  </span>
                )}
              </h2>
            </motion.div>

            <motion.div
              className="space-y-4 text-base md:text-lg text-black leading-relaxed"
              initial={{ opacity: 0, y: 50 }}
              animate={isTextInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <p className="font-cairo">
                {description}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={isTextInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <Link href="/ar-IQ/about">
                <motion.button
                  className="bg-[#0a367b] text-white px-6 py-3 rounded-full text-base font-semibold hover:bg-[#0a367b]/90 transition-all duration-300 shadow-lg font-cairo"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  اعرف المزيد
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
